import type { Color } from '@ui-kit/types'

export { ANIMATION } from '@ui-kit/assets/animations'

export const HEADER_HEIGHT = {
  mobile: 56,
  desktop: 72,
} as const

export const SCREEN_SIZE = {
  S: 360,
  M: 768,
  L: 1024,
  XL: 1280,
  XXL: 1440,
} as const

export type ScreenSize = keyof typeof SCREEN_SIZE

export const BREAKPOINT = {
  S: {
    up: `@media (min-width: ${SCREEN_SIZE.S + 1}px)`,
    down: `@media (max-width: ${SCREEN_SIZE.S}px)`,
  },
  M: {
    up: `@media (min-width: ${SCREEN_SIZE.M + 1}px)`,
    down: `@media (max-width: ${SCREEN_SIZE.M}px)`,
  },
  L: {
    up: `@media (min-width: ${SCREEN_SIZE.L + 1}px)`,
    down: `@media (max-width: ${SCREEN_SIZE.L}px)`,
  },
  XL: {
    up: `@media (min-width: ${SCREEN_SIZE.XL + 1}px)`,
    down: `@media (max-width: ${SCREEN_SIZE.XL}px)`,
  },
  XXL: {
    up: `@media (min-width: ${SCREEN_SIZE.XXL + 1}px)`,
    down: `@media (max-width: ${SCREEN_SIZE.XXL}px)`,
  },
}

export const ACTIVE_OPACITY = 0.7
export const STYLE_TRANSITION_SPEED = 0.4

export const COLOR = {
  // Brand - Primaries
  white: '#FFFFFF',
  deepForest: '#003D2B',
  black: '#00140F',
  paleSpring: '#F2FFC9',
  safepointsGreenLight: '#D8E3B4',

  // Neutrals -> Grays
  grey100: '#F9F7F6',
  grey200: '#F0ECEA',
  grey300: '#DDD8D5',
  grey400: '#BDB8B4',
  grey500: '#9A9795',
  grey600: '#73706E',
  grey700: '#4F4C4A',

  // Shades - Secondaries
  paleSpringLight: '#FAFFE4',
  paleSpringDark: '#C6D1BE',
  deepForestLight: '#E6F1EC',
  deepForestDark: '#002E22',

  // Success
  success: '#30AD36',
  successLight: '#D7F0D9',
  successDark: '#0A3F0D',

  // Error
  error: '#D32F2F',
  errorLight: '#FADADA',
  errorDark: '#4A0B0B',

  // Warning
  warning: '#F57C00',
  warningLight: '#FBE7C6',
  warningDark: '#663D00',

  // Legacy colors (deprecated - to be removed)
  brandDark: '#003D2B',
  brandDarkTransparent: 'rgba(0, 61, 43, 0.05)',
  brandLight: '#ADE6B9',
  brandLime: '#DEF071',

  uiDark: '#00140F',
  uiDarkMedium: 'rgba(0, 20, 15, 0.7)',
  uiDarkDisabled: 'rgba(0, 20, 15, 0.3)',
  uiLight: '#FFFFFF',
  uiLightMedium: 'rgba(255, 255, 255, 0.6)',
  uiLightDisabled: 'rgba(255, 255, 255, 0.38)',
  uiGreyDark: '#999999',
  uiGreyMedium: '#DDDDDD',
  uiGreyLight: '#F3F3F3',
  uiGrayUltralight: '#FAFAFA',
  uiGreen: '#009B67',
  uiGreenLight: '#D9F0E8',
  uiGreenOverlay: 'rgba(0, 155, 103, 0.06)',
  uiGreenMuted: '#A4CCC0',
  uiRed: '#B33636',
  uiRedLight: '#F4E1E1',
  uiRedMuted: '#F7D4D1',
  uiOrange: '#946200',
  uiOrangeLight: '#F4EDE1',
  uiOrangeMuted: '#EDCEB4',

  blueSky: '#24B0FF',

  playground1Dark: '#3D1400',
  playground1Light: '#E5BFAD',
  playground2Dark: '#3D3D00',
  playground2Light: '#E5E5AD',
  playground3Dark: '#003D3D',
  playground3Light: '#ADE5E5',
  playground4Dark: '#00003D',
  playground4Light: '#ADADE5',
  playground5Dark: '#3D003D',
  playground5Light: '#E5ADE5',
  playground6Dark: '#3D0014',
  playground6Light: '#E5ADBF',
  whatsapp: '#25d366',

  transparent: 'transparent',
  backdrop: 'rgba(74, 74, 74, 0.7)',
} as const

export const SPACE = {
  none: 0,
  gutter: 24,
  xxsmall: 4,
  mini: 8,
  xsmall: 10,
  small: 16,
  medium: 20,
  large: 32,
  largeMedium: 40,
  xlarge: 48,
  xlargeMedium: 64,
  xxlarge: 80,
  xxlargeMedium: 112,
  xxxlarge: 160,
} as const

export const ICON_SIZE = {
  xsmall: 16,
  small: 24,
  medium: 40,
  large: 64,
  xlarge: 104,
} as const

export const OPACITY = {
  default: 1,
  high: 0.9,
  medium: 0.7,
  low: 0.4,
  xlow: 0.3,
} as const

export const BORDER_RADIUS = {
  none: 0,
  xsmall: 2,
  small: 4,
  normal: 8,
  medium: 10,
  large: 20,
  xlarge: 32,
  xxlarge: 40,
  xxxlarge: 50,
  full: 9999,
} as const

export const BOX_SHADOW_WEB = '1px 1px 10px rgba(0, 0, 0, 0.05)'

export const FORM_ELEMENT = {
  labelColor: COLOR.grey500,
  borderWidth: 1,
  borderRadius: BORDER_RADIUS.normal,
  borderColor: COLOR.grey300,
  placeholderColor: COLOR.grey500,
  activeColor: COLOR.deepForest, // deprecated
  activeColorName: 'deepForest',
  disabledColor: COLOR.grey500,
  disabledOpacity: 0.4,
  errorColor: COLOR.error, // deprecated
  errorColorName: 'error',
} as const satisfies {
  labelColor: string
  borderWidth: number
  borderRadius: number
  borderColor: string
  placeholderColor: string
  activeColor: string // deprecated
  activeColorName: Color
  disabledColor: string
  disabledOpacity: number
  errorColor: string // deprecated
  errorColorName: Color
}

export const MAX_LIST_WIDTH = 560
